import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import PerformanceBadge from '../components/PerformanceBadge';
import { ArrowLeft, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Map test → (category, value-field, unit) for table cells.
const TEST_META = {
  'Vertical Jump':       { category: 'Power',               field: 'measurement', unit: 'cm' },
  'Standing Long Jump':  { category: 'Power',               field: 'measurement', unit: 'cm' },
  '20m Sprint':          { category: 'Speed',               field: 'time',        unit: 's' },
  '40m Sprint':          { category: 'Speed',               field: 'time',        unit: 's' },
  'T-Test':              { category: 'Agility',             field: 'time',        unit: 's' },
  '5-10-5 Test':         { category: 'Agility',             field: 'time',        unit: 's' },
  'Cooper Test':         { category: 'Endurance',           field: 'distance',    unit: 'm' },
  'Shuttle Run Test':    { category: 'Endurance',           field: 'laps',        unit: 'laps' },
  'Sit and Reach':       { category: 'Flexibility',         field: 'measurement', unit: 'cm' },
  'Squat':               { category: 'Lower Body Strength', field: 'weight',      unit: 'kg' },
  'Deadlift':            { category: 'Lower Body Strength', field: 'weight',      unit: 'kg' },
  'Lunge':               { category: 'Lower Body Strength', field: 'weight',      unit: 'kg' },
  'Hip Thrust':          { category: 'Lower Body Strength', field: 'weight',      unit: 'kg' },
  'Chest Press':         { category: 'Upper Body Strength', field: 'weight',      unit: 'kg' },
  'Bent Over Row':       { category: 'Upper Body Strength', field: 'weight',      unit: 'kg' },
  'Shoulder Press':      { category: 'Upper Body Strength', field: 'weight',      unit: 'kg' },
};

const STATUS_STYLE = {
  Draft: 'bg-gray-100 text-gray-700 border-gray-300',
  Incomplete: 'bg-amber-100 text-amber-800 border-amber-300',
  Complete: 'bg-emerald-100 text-emerald-800 border-emerald-300',
};

// Comma/dot decimal, reject negative. Returns number | '' (invalid) | null.
// `isLaps` additionally rejects non-whole numbers (Shuttle Run Test).
const parseValue = (raw, isLaps = false) => {
  if (raw === '' || raw === null || raw === undefined) return null;
  const cleaned = String(raw).trim().replace(',', '.');
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return '';
  if (isLaps && !Number.isInteger(n)) return '';
  return n;
};

const BulkAssessmentDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // grid[athlete_id][test_type] = { raw, classified: {label, colorKey, reason}, dirty }
  const [grid, setGrid] = useState({});
  const classifyTimers = useRef({});

  useEffect(() => {
    if (!['Admin', 'Staff'].includes(user?.role)) {
      navigate('/dashboard');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bulk-assessments/${id}`);
      setSession(res.data);
      // Seed the grid from existing results.
      const g = {};
      (res.data.athletes || []).forEach(a => {
        g[a.id] = {};
        (res.data.test_types || []).forEach(t => {
          const existing = (res.data.results || []).find(
            r => r.athlete_id === a.id && r.test_type === t
          );
          const meta = TEST_META[t];
          const value = existing?.[meta?.field];
          g[a.id][t] = {
            raw: value != null ? String(value) : '',
            classified: existing?.performance_label
              ? { label: existing.performance_label, colorKey: existing.performance_color, reason: existing.performance_reason }
              : null,
            dirty: false,
          };
        });
      });
      setGrid(g);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const scheduleClassify = useCallback((athleteId, testType, rawValue) => {
    const key = `${athleteId}::${testType}`;
    clearTimeout(classifyTimers.current[key]);
    const numeric = parseValue(rawValue);
    if (numeric === null || numeric === '') return;
    classifyTimers.current[key] = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/assessments/classify`, {
          athlete_id: athleteId,
          test_type: testType,
          result_value: numeric,
          assessment_date: session?.assessment_date,
        });
        setGrid(prev => ({
          ...prev,
          [athleteId]: {
            ...prev[athleteId],
            [testType]: {
              ...prev[athleteId][testType],
              classified: {
                label: res.data.display_label,
                colorKey: res.data.color_key,
                reason: res.data.reason,
              },
            },
          },
        }));
      } catch {
        /* silent — will get authoritative verdict on save */
      }
    }, 400);
  }, [session?.assessment_date]);

  useEffect(() => () => {
    Object.values(classifyTimers.current).forEach(clearTimeout);
  }, []);

  const updateCell = (athleteId, testType, raw) => {
    setGrid(prev => ({
      ...prev,
      [athleteId]: {
        ...prev[athleteId],
        [testType]: {
          ...prev[athleteId][testType],
          raw,
          dirty: true,
          classified: null, // invalidate until classifier re-runs
        },
      },
    }));
    scheduleClassify(athleteId, testType, raw);
  };

  const { totalCells, filledCells, dirtyCount, invalidCells } = useMemo(() => {
    let total = 0, filled = 0, dirty = 0, invalid = 0;
    if (session) {
      session.athletes?.forEach(a => {
        session.test_types?.forEach(t => {
          total += 1;
          const cell = grid[a.id]?.[t];
          if (!cell) return;
          const parsed = parseValue(cell.raw, TEST_META[t]?.field === 'laps');
          if (parsed === '') invalid += 1;
          else if (parsed !== null) filled += 1;
          if (cell.dirty) dirty += 1;
        });
      });
    }
    return { totalCells: total, filledCells: filled, dirtyCount: dirty, invalidCells: invalid };
  }, [grid, session]);

  const handleSave = async () => {
    if (invalidCells > 0) {
      toast.error('Some cells have invalid values (negatives or non-numeric).');
      return;
    }
    // Collect ALL dirty cells that are either: (a) filled with a
    // number — upsert; or (b) empty — skipped server-side.
    const cells = [];
    Object.entries(grid).forEach(([aid, tests]) => {
      Object.entries(tests).forEach(([t, cell]) => {
        if (!cell.dirty) return;
        const meta = TEST_META[t];
        if (!meta) return;
        const parsed = parseValue(cell.raw, meta.field === 'laps');
        if (parsed === '' || parsed === null) return; // skip invalid / empty
        const payload = {
          athlete_id: aid,
          test_type: t,
          category: meta.category,
          [meta.field]: parsed,
        };
        cells.push(payload);
      });
    });
    if (cells.length === 0) {
      toast.info('Nothing to save.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/bulk-assessments/${id}/results`, { cells });
      toast.success(`Saved · ${res.data.created} new, ${res.data.updated} updated · ${res.data.status}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8 flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
        </div>
      </Layout>
    );
  }
  if (!session) {
    return (
      <Layout>
        <div className="p-8"><p className="text-rose-600">Session not found.</p></div>
      </Layout>
    );
  }

  const total = (session.athletes?.length || 0) * (session.test_types?.length || 0);
  const completionPct = total ? Math.round((filledCells / total) * 100) : 0;

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <Button variant="ghost" size="sm" onClick={() => navigate('/assessments/bulk')}
                className="mb-3" data-testid="bulk-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to sessions
        </Button>

        {/* Header */}
        <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100"
                data-testid="bulk-detail-title">
              {session.session_name}
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-500 dark:text-gray-400">
              <span>{session.assessment_date}</span>
              {session.squad && <Badge variant="outline" className="text-[10px]">{session.squad}</Badge>}
              {session.season && <Badge variant="outline" className="text-[10px]">{session.season}</Badge>}
              <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${STATUS_STYLE[session.status] || STATUS_STYLE.Draft}`}
                    data-testid="bulk-detail-status">
                {session.status}
              </span>
              <span className="font-medium text-gray-700 dark:text-gray-300"
                    data-testid="bulk-detail-completion">
                {completionPct}% filled ({filledCells}/{total})
              </span>
            </div>
          </div>
          <Button
            className="bg-pfap-green hover:bg-pfap-green/90"
            onClick={handleSave}
            disabled={saving || dirtyCount === 0}
            data-testid="bulk-save-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving…' : `Save ${dirtyCount || ''} change${dirtyCount === 1 ? '' : 's'}`}
          </Button>
        </div>

        {/* Grid */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Results grid</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {session.athletes?.length === 0 ? (
              <p className="text-gray-500 py-6 text-center">You do not have access to any athletes on this session.</p>
            ) : (
            <table className="w-full border-collapse min-w-[640px]" data-testid="bulk-grid">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                  <th className="sticky left-0 bg-white dark:bg-gray-950 z-10 px-3 py-2 w-48">Athlete</th>
                  {session.test_types?.map(t => (
                    <th key={t} className="px-3 py-2 min-w-[130px]">
                      <div className="flex flex-col">
                        <span className="normal-case text-[13px] font-semibold text-gray-800 dark:text-gray-200">{t}</span>
                        <span className="text-[10px] text-gray-400">{TEST_META[t]?.unit}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {session.athletes?.map(a => {
                  const row = grid[a.id] || {};
                  const rowFilled = (session.test_types || [])
                    .filter(t => parseValue(row[t]?.raw, TEST_META[t]?.field === 'laps') !== null
                      && parseValue(row[t]?.raw, TEST_META[t]?.field === 'laps') !== '').length;
                  const allRow = session.test_types?.length || 0;
                  const rowStatus = rowFilled === 0 ? 'Draft'
                                  : rowFilled === allRow ? 'Complete' : 'Incomplete';
                  return (
                    <tr key={a.id} className="border-b last:border-0 dark:border-gray-800"
                        data-testid={`bulk-row-athlete-${a.id}`}>
                      <td className="sticky left-0 bg-white dark:bg-gray-950 z-10 px-3 py-3 align-top">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{a.full_name}</div>
                        <div className="flex items-center gap-1 mt-1">
                          {a.squad && <span className="text-[10px] text-gray-500">{a.squad}</span>}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${STATUS_STYLE[rowStatus]}`}>
                            {rowStatus}
                          </span>
                        </div>
                      </td>
                      {session.test_types?.map(t => {
                        const cell = row[t] || { raw: '', classified: null };
                        const parsed = parseValue(cell.raw, TEST_META[t]?.field === 'laps');
                        const invalid = parsed === '';
                        return (
                          <td key={t} className="px-3 py-3 align-top" data-testid={`bulk-cell-${a.id}-${t}`}>
                            <Input
                              value={cell.raw}
                              onChange={e => updateCell(a.id, t, e.target.value)}
                              placeholder="—"
                              inputMode={TEST_META[t]?.field === 'laps' ? 'numeric' : 'decimal'}
                              className={`h-8 ${invalid ? 'border-rose-400 focus-visible:ring-rose-400' : ''}`}
                              data-testid={`bulk-input-${a.id}-${t}`}
                            />
                            {cell.classified && (
                              <div className="mt-1">
                                <PerformanceBadge
                                  label={cell.classified.label}
                                  colorKey={cell.classified.colorKey}
                                  reason={cell.classified.reason}
                                  testId={`bulk-badge-${a.id}-${t}`}
                                />
                              </div>
                            )}
                            {invalid && (
                              <p className="text-[10px] text-rose-500 mt-1">
                                {TEST_META[t]?.field === 'laps' ? 'Must be a whole number ≥ 0' : 'Must be ≥ 0'}
                              </p>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            )}
          </CardContent>
        </Card>

        {session.notes && (
          <Card className="mt-4">
            <CardContent className="pt-4 text-sm text-gray-600 dark:text-gray-400">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Session notes</p>
              {session.notes}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default BulkAssessmentDetail;
